import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";
import IOSSwitch from "../IOSSwitch";
import { ColumnVisibility } from "./parts/projectListColumnWidths";

export type ToggleableColumnKey = Exclude<keyof ColumnVisibility, "showName">;

export const COLUMN_TOGGLES: { key: ToggleableColumnKey; labelKey: string }[] =
  [
    { key: "showJobsite", labelKey: "JOBSITE_NAME" },
    { key: "showPhotographer", labelKey: "PHOTOGRAPHER" },
    { key: "showImages", labelKey: "IMAGES" },
    { key: "showType", labelKey: "TYPE" },
    { key: "showStatus", labelKey: "STATUS" },
    { key: "showCaptureDate", labelKey: "CAPTURED_DATE" },
    { key: "showUploadDate", labelKey: "UPLOAD_DATE" },
  ];

type Props = {
  expanded: boolean;
  onToggleExpanded: () => void;
  values: Partial<Record<ToggleableColumnKey, boolean | null>>;
  onChange: (key: ToggleableColumnKey, checked: boolean) => void;
  contrastColor: string;
};

const ProjectListColumnSettings = (props: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <Box
        onClick={props.onToggleExpanded}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "6px",
          cursor: "pointer",
          marginTop: "20px",
          paddingLeft: "15px",
          paddingRight: "8px",
        }}
      >
        <span>{t("PROJECT_LIST_COLUMN_SETTINGS")}</span>
        <ExpandMoreIcon
          sx={{
            color: props.contrastColor,
            transform: props.expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Box>
      <Collapse in={props.expanded}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            columnGap: "12px",
            rowGap: "8px",
            padding: "8px 15px 0 15px",
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
          >
            <IOSSwitch checked disabled />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {capitalize(t("NAME"))}
            </span>
          </Box>
          {COLUMN_TOGGLES.map(({ key, labelKey }) => (
            <Box
              key={key}
              sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
            >
              <IOSSwitch
                checked={!!props.values[key]}
                onChange={(e) => props.onChange(key, e.target.checked)}
              />
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t(labelKey)}
              </span>
            </Box>
          ))}
        </Box>
      </Collapse>
    </>
  );
};

export default ProjectListColumnSettings;
